function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { withRouter } from "react-router";
import { Container, FlexboxContainer, CssContainer } from "../../components/container";
import { PageHeader, Ribbon, Card, Pill, Empty, Search, Filters, Delta, Pending } from "../../components/analytics-kit";
import PubSub from "pubsub-js";

// territory-common deltas ({text, dir, pct}) in the kit's shape; null stays null ("no comparison").
const kitDelta = d => d && d.pct != null ? {
  dir: d.dir,
  pct: d.pct
} : null;
import ComparisonPicker from "../../components/comparison-picker";
import StatusFilter from "../../components/status-filter";
import PlunkDropDown from "../../components/plunk-dropdown";
import { Loader, ChartLoader } from "../../components/loaders";
import Error from "../../components/error";
import Chart from "../../components/chart";
import { hasTenantFeature } from "../../functions/tenant-features";
import { rangeLabelFor, resolveBusinessModel } from "./territory-common";
import { RANGES, num, usd, aov, fetchSales, bucketFor, trendLabel, deltaOf, deltaOfPaced, comparisonQs, hasPrevTrend, PREV_LINE_COLOR, titleCase, accountLensKv, readAcctLens, writeAcctLens, cleanTitle, US_STATES } from "./territory-common";
import { AccountFilterControl, AccountFilterBanner } from "./account-filter";
import { Link } from "react-router-dom";
import account from "../../data/account";
import ServiceFactory from "../../service";
import { ActivityComparisonMenu } from "../../components/container-activity-menus";
import compLabel from "../../functions/comp-label";
import { getGlobalRange, getGlobalComp, subscribePeriod, unsubscribePeriod } from "../../functions/period-store";
import { AtpHistoryChart } from "../inventory/charts";

/* ---- Products pages shared helpers (product + series pages; styles: sass/v2/_products-pages.scss, `pp-`) ---- */

// Chart colors (approved mockups): this period coral, comparison slate, forecast teal.
export const PP_CORAL = "#ff6358";
export const PP_COMPARE = "#9fb3c8";
export const PP_FORECAST = "#14b8a6";

/** Money without cents for headlines: $182,197. */
export function usd0(v) {
  const n = Number(v) || 0;
  return (n < 0 ? "-$" : "$") + Math.round(Math.abs(n)).toLocaleString("en-US");
}

/** Short money for tiles and summaries: $240K, $2.28M. */
export function usdShort(v) {
  const n = Number(v) || 0;
  const a = Math.abs(n);
  if (a >= 1e6) return "$" + (Math.round(n / 1e6 * 100) / 100).toLocaleString("en-US") + "M";
  if (a >= 1e4) return "$" + Math.round(n / 1e3).toLocaleString("en-US") + "K";
  if (a >= 1e3) return "$" + (Math.round(n / 1e3 * 10) / 10).toLocaleString("en-US") + "K";
  return usd0(n);
}

/** Display title: clean the feed's quote cruft, Title Case ALL-CAPS titles (LEE's feed). */
export function displayTitle(v) {
  const t = cleanTitle(v);
  if (t == null || t === "") return t;
  const s = String(t);
  return /[A-Z]/.test(s) && !/[a-z]/.test(s) ? titleCase(s) : s;
}

/** Explorer rows come back as an array or wrapped ({items}/{rows}). */
export function explorerRows(r) {
  if (Array.isArray(r)) return r;
  if (r && Array.isArray(r.items)) return r.items;
  if (r && Array.isArray(r.rows)) return r.rows;
  return [];
}

/** "96 W × 40 D × 32 H in" from text carrying "W96 D40 H32" (or "W 96" / "96W"). */
export function dimensionsFrom(text) {
  const s = String(text || "");
  const pick = k => {
    const m = s.match(new RegExp("\\b" + k + "\\s*:?\\s*(\\d+(?:\\.\\d+)?)\\s*\"?", "i")) || s.match(new RegExp("\\b(\\d+(?:\\.\\d+)?)\\s*\"?\\s*" + k + "\\b", "i"));
    return m ? m[1] : null;
  };
  const w = pick("W"),
    d = pick("D"),
    h = pick("H");
  if (!w || !h) return null;
  return [w ? `${w} W` : null, d ? `${d} D` : null, h ? `${h} H` : null].filter(Boolean).join(" × ") + " in";
}

/** Card that shows the top `limit` rows with an "All N" toggle. children(rowsShown). */
export function CappedCard({
  title,
  hint,
  rows,
  limit = 5,
  span = 4,
  cls = "",
  allLabel,
  children
}) {
  const [open, setOpen] = React.useState(false);
  const shown = open ? rows : rows.slice(0, limit);
  const more = rows.length > limit ? open ? "Show fewer" : allLabel || `All ${num(rows.length)}` : null;
  return /*#__PURE__*/React.createElement(Card, {
    title: title,
    hint: hint,
    span: span,
    cls: `pp-card ${cls}`,
    more: more,
    onMore: () => setOpen(!open)
  }, children(shown));
}

/** Compact two-line row for the list cards (name + sub on the left, value on the right). */
export function ListRow({
  name,
  sub,
  value,
  onClick,
  title
}) {
  return /*#__PURE__*/React.createElement("div", {
    className: `pp-row${onClick ? " clickable" : ""}`,
    onClick: onClick,
    title: title
  }, /*#__PURE__*/React.createElement("span", {
    className: "pp-row-l"
  }, /*#__PURE__*/React.createElement("span", {
    className: "pp-row-name"
  }, name), sub ? /*#__PURE__*/React.createElement("span", {
    className: "pp-row-sub"
  }, sub) : null), value != null ? /*#__PURE__*/React.createElement("span", {
    className: "pp-row-v"
  }, value) : null);
}

/** Ribbon in the mockup's shape: label, value, one sub-line (no "no comparison" filler). */
export function PPRibbon({
  tiles
}) {
  const list = tiles.filter(Boolean);
  return /*#__PURE__*/React.createElement("div", {
    className: "pp-ribbon"
  }, list.map(t => /*#__PURE__*/React.createElement("div", {
    className: "pp-kpi",
    key: t.l,
    title: t.t || undefined
  }, /*#__PURE__*/React.createElement("span", {
    className: "l"
  }, t.l), /*#__PURE__*/React.createElement("b", {
    className: "v"
  }, t.v), t.d ? /*#__PURE__*/React.createElement("span", {
    className: "s"
  }, /*#__PURE__*/React.createElement(Delta, {
    d: t.d
  }), t.ds ? ` ${t.ds}` : "") : t.s ? /*#__PURE__*/React.createElement("span", {
    className: "s"
  }, t.s) : /*#__PURE__*/React.createElement("span", {
    className: "s"
  }, "\xA0"))));
}

/** Revenue columns for this period + dashed previous-period line (one money axis, no units axis). */
export function salesByPeriodData(trend, bucket, comp) {
  if (!trend || trend.length < 2) return null;
  const withPrev = hasPrevTrend(trend);
  const prevName = (compLabel(comp) || "Previous period").replace(/^Unavailable$/, "Previous period");
  const head = withPrev ? ["Period", "This period", prevName.charAt(0) + prevName.slice(1).toLowerCase()] : ["Period", "This period"];
  const data = [head];
  trend.forEach(t => {
    const rev = Math.round(Number(t.totalAmount) || 0);
    if (withPrev) data.push([trendLabel(t.period, bucket), rev, Math.round(Number(t.prevTotalAmount) || 0)]);else data.push([trendLabel(t.period, bucket), rev]);
  });
  data.withPrev = withPrev;
  return data;
}
export const salesByPeriodOptions = withPrev => ({
  seriesType: "bars",
  series: withPrev ? {
    0: {
      color: PP_CORAL,
      tooltipFormat: "{0:c0}"
    },
    1: {
      type: "line",
      color: PP_COMPARE,
      lineDashStyle: [4, 3],
      tooltipFormat: "{0:c0}"
    }
  } : {
    0: {
      color: PP_CORAL,
      tooltipFormat: "{0:c0}"
    }
  },
  vAxis: {
    format: "short"
  },
  legend: {
    position: withPrev ? "top" : "none"
  },
  pointSize: 0,
  backgroundColor: "transparent"
});
export const bucketWord = bucket => bucket === "day" ? "day" : bucket === "week" ? "week" : "month";

/*
 * Product Detail — Phase A of the Product Performance spec
 * (PlunkDocs specs/PRODUCT_PERFORMANCE_UI.md).
 *
 * Route: /Territory/:range/product/:id — opened from any Top Products table
 * (rep detail, dealer detail, market detail).
 *
 * Server-scoped via the 1.1.17/1.1.19 product params:
 *   /sales/totals/{range}?product=X            → KPI header
 *   /sales/trend/{range}?product=X             → sales trend (headline)
 *   /sales/forecast/{range}?product=X&months=12 → monthly actuals + projected
 *                                                months with ±1.96σ bands
 *   /sales/totals/{range}?product=X&customerType=retail|wholesale
 *                                              → channel split (shown only
 *                                                when both channels have sales)
 *
 * Product identity: router state carries the title from click-throughs;
 * deep links resolve via /sales/product-info/{id} (title + image +
 * description — 1.1.35). Breakouts: by-rep / by-dealer / by-market tables
 * via ?product= on the roster endpoints (Service.Product 1.1.21).
 *
 * Contacts Interested (2026-07-30 additions): tracked contacts who viewed
 * THIS product — /v1/activity/contacts/product/{id}/{range}?$top=100 (new
 * SQL-side roster; NEVER the legacy /metrics/product/contacts family, it
 * materializes tenant-wide history and 504s). personId is a raw >2^53
 * long — safe only because ServiceFactory parses with JSONbig; stringify
 * for the Contact Detail route. Section hides entirely when empty.
 */

function fetchActivity(api) {
  const Service = ServiceFactory.getType(api);
  return new Service().getAsync();
}

// Inventory Planner (INVENTORY_UX.md) lives at /v1/inventory (ActivityService);
// the `product` base is the bare /v1 root, so this resolves to /v1/inventory/*.
function fetchInv(api) {
  const Service = ServiceFactory.getType(api, "product");
  return new Service().getAsync();
}
class ProductDetailViewComponent extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "setAcctLens", (mode, ids) => {
      writeAcctLens((account.get() || {}).tenantId, mode, ids);
      this.setState({
        acctMode: mode,
        acctIds: ids,
        data: null,
        error: false
      }, () => this.load());
    });
    _defineProperty(this, "removeAcct", id => this.setAcctLens(this.state.acctMode, this.state.acctIds.filter(x => String(x) !== String(id))));
    _defineProperty(this, "clearAcct", () => this.setAcctLens(this.state.acctMode, []));
    _defineProperty(this, "onRange", e => {
      this.props.history.push({
        pathname: `${this.basePath()}/${e.target.value}/product/${encodeURIComponent(this.productId())}`,
        state: this.navState()
      });
    });
    _defineProperty(this, "onCompSelected", e => {
      const comp = e.detail.item.dataset.value;
      if (comp === "unavailable" || comp === this.state.comp) return;
      this.setState({
        comp,
        data: null,
        error: false
      }, () => this.load());
    });
    _defineProperty(this, "back", () => {
      this.props.history.goBack();
    });
    _defineProperty(this, "openRep", repCode => {
      if (!repCode) return;
      this.props.history.push(`/Territory/${this.range()}/rep/${encodeURIComponent(repCode)}`);
    });
    _defineProperty(this, "openCustomer", accountNumber => {
      if (!accountNumber) return;
      this.props.history.push(`/Territory/${this.range()}/customer/${encodeURIComponent(accountNumber)}`);
    });
    _defineProperty(this, "openRegion", code => {
      if (!code) return;
      this.props.history.push(`/Territory/${this.range()}/region/${encodeURIComponent(code)}`);
    });
    _defineProperty(this, "openProduct", (pid, title) => {
      if (!pid) return;
      this.props.history.push({
        pathname: `${this.basePath()}/${this.range()}/product/${encodeURIComponent(pid)}`,
        state: title ? {
          title
        } : undefined
      });
    });
    const lens = readAcctLens((account.get() || {}).tenantId);
    this.state = {
      data: null,
      error: false,
      status: [],
      comp: getGlobalComp("prev_period"),
      descExpanded: false,
      acctMode: lens.mode,
      acctIds: lens.ids,
      acctSource: []
    };
    this._alive = true;
  }
  async loadAcctSource() {
    try {
      const rows = await fetchSales(`/sales/customers/${this.range()}?top=200`);
      if (this._alive) this.setState({
        acctSource: Array.isArray(rows) ? rows : []
      });
    } catch (e) {/* optional */}
  }
  range() {
    return this.props.params && this.props.params.range || getGlobalRange();
  }
  productId() {
    return decodeURIComponent(this.props.params && this.props.params.id || "");
  }
  productTitle() {
    const st = this.props.location && this.props.location.state;
    return st && st.title || null;
  }
  componentDidMount() {
    // Retail workspaces have no B2B accounts or order statuses: the two filters are not shown.
    resolveBusinessModel().then(m => {
      if (m) this.setState({
        businessModel: m
      });
    }).catch(() => {});
    this.load();
    this.loadAcctSource();
    this._periodTok = subscribePeriod(({
      range,
      comp
    }) => {
      if (!this._alive || !range) return;
      if (comp && comp !== this.state.comp) this.setState({
        comp
      });
      const target = `${this.basePath()}/${range}/product/${encodeURIComponent(this.productId())}`;
      if (window.location.pathname === target) {
        this.setState({
          data: null,
          error: false,
          descExpanded: false
        }, () => this.load());
      } else {
        this.props.history.push({
          pathname: target,
          state: this.navState()
        });
      }
    });
  }
  componentWillUnmount() {
    this._alive = false;
    unsubscribePeriod(this._periodTok);
  }
  componentDidUpdate(prev) {
    const prevKey = `${prev.params && prev.params.range || ""}|${prev.params && prev.params.id || ""}`;
    const key = `${this.props.params && this.props.params.range || ""}|${this.props.params && this.props.params.id || ""}`;
    if (prevKey !== key) {
      this.setState({
        data: null,
        error: false,
        descExpanded: false
      });
      this.load();
    }
  }
  async load() {
    const range = this.range();
    const comp = this.state.comp;
    const id = encodeURIComponent(this.productId());
    const bucket = bucketFor(range);
    const st = (this.state.status || []).join(",");
    const acctKv = accountLensKv(this.state.acctMode, this.state.acctIds);
    // ws appends the status filter AND the account lens (architecture B — the /sales/*
    // routes honor excludeCustomers/onlyCustomers) to every scoped fetch.
    const ws = u => {
      let out = st ? u + (u.includes("?") ? "&" : "?") + "status=" + encodeURIComponent(st) : u;
      if (acctKv) out += (out.includes("?") ? "&" : "?") + acctKv;
      return out;
    };
    try {
      const [totals, trend, forecast, retail, wholesale, info, byRep, byDealer, byMarket, copurchase] = await Promise.all([fetchSales(ws(`/sales/totals/${range}?product=${id}${comparisonQs(range, false, comp)}`)), fetchSales(ws(`/sales/trend/${range}?bucket=${bucket}&product=${id}${comparisonQs(range, false, comp)}`)), fetchSales(ws(`/sales/forecast/${range}?product=${id}&months=12`)), fetchSales(ws(`/sales/totals/${range}?product=${id}&customerType=retail`)), fetchSales(ws(`/sales/totals/${range}?product=${id}&customerType=wholesale`)), fetchSales(`/sales/product-info/${id}`).catch(() => null), fetchSales(ws(`/sales/reps/${range}?product=${id}`)).catch(() => []), fetchSales(ws(`/sales/customers/${range}?top=100&product=${id}`)).catch(() => []), fetchSales(ws(`/sales/territories/${range}?product=${id}`)).catch(() => []), fetchSales(`/sales/copurchase?product=${id}&limit=8`).catch(() => null)]);
      if (this._alive) this.setState({
        data: {
          totals,
          trend,
          forecast,
          retail,
          wholesale,
          info,
          byRep,
          byDealer,
          byMarket,
          copurchase,
          bucket
        }
      });
      // Explorer row for this product (category, website views, family, and an image
      // with a same-series fallback when the product has none). New endpoint: fails soft.
      fetchSales(`/sales/explorer/products/${range}?q=${id}&top=1`).then(r => {
        const pid = this.productId();
        const row = explorerRows(r).find(x => x && (String(x.productId) === pid || String(x.sku) === pid));
        if (this._alive && row) this.setState(st => st.data ? {
          data: {
            ...st.data,
            explorer: row
          }
        } : null);
      }).catch(() => {});
      // Contacts Interested loads separately — a hiccup here must not
      // block the sales page (the section just stays hidden).
      fetchActivity(`/contacts/product/${id}/${range}?$top=100`).then(rows => {
        if (this._alive) this.setState(st => st.data ? {
          data: {
            ...st.data,
            contacts: rows || []
          }
        } : null);
      }).catch(() => {});
      // Configurations breakdown (PRODUCT_CONFIGURATIONS.md §2.4) —
      // non-blocking; section renders only when the style has
      // component rows in range (no empty tab for non-config styles).
      fetchSales(`/product/configurations/${range}?$groupBy=configuration&styleId=${id}&$top=50`).then(r => {
        if (this._alive) this.setState(st => st.data ? {
          data: {
            ...st.data,
            configs: r && r.items || []
          }
        } : null);
      }).catch(() => {});
      // Inventory position + ATP history (INVENTORY_UX.md) — non-blocking,
      // gated on the inventory feature; the panel then renders only when this
      // product actually has feed data (hide-if-no-data, DS 2026-09-07).
      if (hasTenantFeature("inventory")) {
        fetchInv(`/inventory/products/${id}/trend?range=this_year&grain=day`).then(r => {
          if (this._alive && r && (r.inventory || r.sales)) this.setState(st => st.data ? {
            data: {
              ...st.data,
              inventory: r
            }
          } : null);
        }).catch(() => {});
      }
    } catch (e) {
      if (this._alive) this.setState({
        error: true
      });
    }
  }

  // Keep the current namespace (/Products for retail/product entry, /Territory
  // when opened from a territory table) when changing the range.
  basePath() {
    try {
      return (window.location.pathname || "").indexOf("/Products/") === 0 ? "/Products" : "/Territory";
    } catch (e) {
      return "/Territory";
    }
  }
  // Product family/series key: carried in router state from the series page; else the
  // explorer row's family when the endpoint serves one.
  productFamily() {
    const st = this.props.location && this.props.location.state;
    if (st && st.family) return st.family;
    const ex = this.state.data && this.state.data.explorer;
    return ex && (ex.family || ex.series) || null;
  }
  navState() {
    const st = this.props.location && this.props.location.state;
    const out = {};
    if (this.productTitle()) out.title = this.productTitle();
    if (st && st.family) out.family = st.family;
    return Object.keys(out).length ? out : undefined;
  }
  // Monthly actuals + projection with the ±1.96σ band rendered as a shaded
  // range under the forecast line (actual rows carry null forecast).
  forecastChartData(rows) {
    if (!rows || rows.length < 2) return null;
    const anyForecast = rows.some(r => r.isForecast);
    if (!anyForecast) return null;
    const data = [["Period", "Sold", "Expected", {
      role: "interval"
    }, {
      role: "interval"
    }]];
    // Keep the chart readable: the last 6 actual months, then every forecast month.
    const actual = rows.filter(r => !r.isForecast).slice(-6);
    const future = rows.filter(r => r.isForecast);
    actual.concat(future).forEach(r => {
      const period = trendLabel(r.period, "month");
      const amt = Math.round(Number(r.totalAmount) || 0);
      if (r.isForecast) data.push([period, null, amt, Math.round(Number(r.totalAmountLower) || 0), Math.round(Number(r.totalAmountUpper) || 0)]);else data.push([period, amt, null, null, null]);
    });
    return data;
  }
  forecastSummary(rows) {
    const f = (rows || []).filter(r => r.isForecast);
    if (!f.length) return null;
    const avg = k => f.reduce((a, r) => a + (Number(r[k]) || 0), 0) / f.length;
    const mid = avg("totalAmount"),
      lo = avg("totalAmountLower"),
      hi = avg("totalAmountUpper");
    const total = f.reduce((a, r) => a + (Number(r.totalAmount) || 0), 0);
    const units = f.reduce((a, r) => a + (Number(r.quantity) || 0), 0);
    const range = hi > lo ? `, between ${usdShort(Math.max(0, lo))} and ${usdShort(hi)}` : "";
    return {
      months: f.length,
      total,
      units,
      line: `About ${usdShort(mid)} a month expected${range}. Based on this product's past sales and the time of year.`
    };
  }
  askAbout(label) {
    PubSub.publish("plunk-assistant-toggle", {
      starter: `Tell me about ${label}: how is it selling, who buys it, and what should I watch?`
    });
  }
  render() {
    // Product detail is model-agnostic: retail sees the universal sections
    // (KPIs, trend, forecast, customers, contacts, configs); the rep/
    // territory sections below self-hide when their data is empty.
    const isMfg = hasTenantFeature("territoryRep");
    const {
      data,
      error
    } = this.state;
    if (error) return /*#__PURE__*/React.createElement(Error, null);
    if (!data) return /*#__PURE__*/React.createElement(Loader, null);
    const id = this.productId();
    const range = this.range();
    const {
      totals,
      trend,
      forecast,
      retail,
      wholesale,
      info,
      byRep,
      byDealer,
      byMarket,
      copurchase,
      inventory,
      bucket,
      explorer
    } = data;
    const rawTitle = this.productTitle() || info && info.title || explorer && explorer.title || null;
    const title = displayTitle(rawTitle);
    const sku = info && info.sku || explorer && explorer.sku || id;
    const imageLink = info && info.imageLink || explorer && explorer.imageLink || null;
    const family = this.productFamily();
    // Feed categories can arrive HTML-escaped ("Furniture &gt; Sofas"); show "Furniture › Sofas".
    const unesc = v => String(v).replace(/&gt;|&#62;/g, ">").replace(/&lt;/g, "<").replace(/&amp;/g, "&").replace(/\s*>\s*/g, " \u203a ");
    const category = explorer && explorer.category ? displayTitle(unesc(explorer.category)) : null;
    const desc = info && String(info.description || "").trim();
    const dims = dimensionsFrom(desc) || dimensionsFrom(rawTitle);
    // A description that is only the size code ("W96 D40 H32") is already shown as the size chip.
    const descText = dims && desc && !desc.replace(/\b[WDH]\s*\d+(\.\d+)?\s*("|in)?/gi, "").replace(/[\s,;x×]+/g, "") ? "" : desc;
    const units = Number(totals.quantity) || 0;
    const avgPrice = units > 0 ? (Number(totals.totalAmount) || 0) / units : null;
    const repsRows = (byRep || []).filter(r => Number(r.orders) > 0);
    const dealerRows = (byDealer || []).filter(r => Number(r.orders) > 0);
    const marketRows = (byMarket || []).filter(r => Number(r.orders) > 0).sort((a, b) => (Number(b.totalAmount) || 0) - (Number(a.totalAmount) || 0));
    const states = marketRows.filter(t => US_STATES[String(t.region || "").toUpperCase()]).length;
    const coItems = copurchase && copurchase.items || [];
    const chartData = salesByPeriodData(trend, bucket, this.state.comp);
    const forecastData = this.forecastChartData(forecast);
    const fc = this.forecastSummary(forecast);
    const retailAmt = Number(retail && retail.totalAmount) || 0;
    const wholesaleAmt = Number(wholesale && wholesale.totalAmount) || 0;
    const showChannels = retailAmt > 0 && wholesaleAmt > 0;
    const views = explorer && explorer.views != null ? Number(explorer.views) : null;
    const label = title ? `${sku} · ${title}` : sku;
    const compWord = (compLabel(this.state.comp) || "").toLowerCase().replace("previous", "last").replace("unavailable", "");
    const configs = data.configs || [];
    const contacts = data.contacts || [];
    const buyerWord = isMfg ? "Dealers" : "Customers";
    const chips = [category, dims, avgPrice ? `Avg price sold ${usd0(avgPrice)}` : null, states > 0 ? `Sold in ${num(states)} state${states === 1 ? "" : "s"}` : null, showChannels ? `Retail ${usdShort(retailAmt)} · Wholesale ${usdShort(wholesaleAmt)}` : null].filter(Boolean);
    return /*#__PURE__*/React.createElement("div", {
      className: "territory-view territory-detail product-detail wa-view pp-view"
    }, /*#__PURE__*/React.createElement("header", {
      className: "pp-top"
    }, /*#__PURE__*/React.createElement("div", {
      className: "pp-top-title"
    }, /*#__PURE__*/React.createElement("span", {
      className: "pp-crumbs"
    }, /*#__PURE__*/React.createElement(Link, {
      to: `/Products/${range}`
    }, "Products"), family ? /*#__PURE__*/React.createElement(React.Fragment, null, " \xB7 ", /*#__PURE__*/React.createElement(Link, {
      to: `/Products/${range}/family/${encodeURIComponent(family)}`
    }, "Series ", family)) : null), /*#__PURE__*/React.createElement("h1", null, label)), this.state.businessModel === "retail" ? null : /*#__PURE__*/React.createElement("span", {
      className: "pp-filters"
    }, /*#__PURE__*/React.createElement(StatusFilter, {
      range: range,
      value: this.state.status,
      onChange: v => this.setState({
        status: v,
        data: null,
        error: false
      }, () => this.load())
    }), /*#__PURE__*/React.createElement(AccountFilterControl, {
      customers: this.state.acctSource || [],
      mode: this.state.acctMode,
      ids: this.state.acctIds,
      onApply: this.setAcctLens
    }))), /*#__PURE__*/React.createElement("section", {
      className: "pp-head"
    }, /*#__PURE__*/React.createElement("span", {
      className: "pp-img"
    }, imageLink ? /*#__PURE__*/React.createElement("img", {
      src: imageLink,
      alt: "",
      loading: "lazy",
      onError: e => {
        e.target.style.display = "none";
      }
    }) : /*#__PURE__*/React.createElement("i", {
      className: "material-icons",
      "aria-hidden": "true"
    }, "weekend")), /*#__PURE__*/React.createElement("div", {
      className: "pp-head-main"
    }, chips.length ? /*#__PURE__*/React.createElement("span", {
      className: "pp-chips"
    }, chips.map(c => /*#__PURE__*/React.createElement("span", {
      key: c,
      className: "pp-chip"
    }, c))) : null, descText ? /*#__PURE__*/React.createElement("div", {
      className: "pp-desc"
    }, /*#__PURE__*/React.createElement("p", {
      className: this.state.descExpanded ? "" : "clamp"
    }, descText), descText.length > 200 && /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "pp-link",
      onClick: () => this.setState({
        descExpanded: !this.state.descExpanded
      })
    }, this.state.descExpanded ? "Less" : "More")) : null), /*#__PURE__*/React.createElement("div", {
      className: "pp-actions"
    }, /*#__PURE__*/React.createElement("span", {
      className: "pp-btns"
    }, info && info.link ? /*#__PURE__*/React.createElement("a", {
      className: "pp-btn",
      href: info.link,
      target: "_blank",
      rel: "noopener noreferrer"
    }, "View on website") : null, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "pp-btn",
      onClick: () => this.askAbout(label)
    }, "Ask about this product")))), this.state.acctIds && this.state.acctIds.length > 0 && /*#__PURE__*/React.createElement(CssContainer, {
      cls: "margin-squeeze"
    }, /*#__PURE__*/React.createElement(AccountFilterBanner, {
      customers: this.state.acctSource || [],
      mode: this.state.acctMode,
      ids: this.state.acctIds,
      onRemove: this.removeAcct,
      onClear: this.clearAcct
    })), /*#__PURE__*/React.createElement(PPRibbon, {
      tiles: [{
        l: "Revenue",
        v: usd0(totals.totalAmount),
        d: kitDelta(deltaOfPaced(totals.totalAmount, totals.prevTotalAmount, range)),
        ds: compWord ? `vs ${compWord}` : "",
        s: rangeLabelFor(range, RANGES)
      }, {
        l: "Units",
        v: num(totals.quantity),
        d: kitDelta(deltaOfPaced(totals.quantity, totals.prevQuantity, range)),
        s: `${num(totals.orders)} orders`
      }, {
        l: `${buyerWord} buying`,
        v: num(totals.customers),
        s: totals.orders ? `${aovShort(totals)} average order` : null
      }, views != null ? {
        l: "Website views",
        v: num(views)
      } : null, fc ? {
        l: `Next ${fc.months} months`,
        v: usdShort(fc.total),
        s: fc.units > 0 ? `about ${num(Math.round(fc.units))} units` : "expected sales"
      } : null]
    }), /*#__PURE__*/React.createElement("div", {
      className: "wa-grid"
    }, /*#__PURE__*/React.createElement(Card, {
      title: `Sales by ${bucketWord(bucket)}`,
      hint: "hover for values",
      span: forecastData ? 6 : 12,
      cls: "pp-card"
    }, chartData ? /*#__PURE__*/React.createElement(Chart, {
      width: "100%",
      height: "260px",
      chartType: "ComboChart",
      loader: /*#__PURE__*/React.createElement(ChartLoader, null),
      data: chartData,
      options: salesByPeriodOptions(chartData.withPrev)
    }) : /*#__PURE__*/React.createElement(Empty, null, "Not enough sales in this period to chart.")), forecastData && /*#__PURE__*/React.createElement(Card, {
      title: "What's coming",
      hint: "shaded band is the likely range",
      span: 6,
      cls: "pp-card"
    }, /*#__PURE__*/React.createElement(Chart, {
      width: "100%",
      height: "260px",
      chartType: "ComboChart",
      loader: /*#__PURE__*/React.createElement(ChartLoader, null),
      data: forecastData,
      options: {
        seriesType: "bars",
        // Forecast v2 (Service.Product 1.1.34): dampened seasonal index over the
        // trailing baseline; under ~13 months of history it is a flat trailing average.
        series: {
          0: {
            color: PP_CORAL,
            tooltipFormat: "{0:c0}"
          },
          1: {
            type: "line",
            color: PP_FORECAST,
            lineDashStyle: [6, 3],
            tooltipFormat: "{0:c0}"
          }
        },
        vAxis: {
          format: "short"
        },
        legend: {
          position: "top"
        },
        pointSize: 0,
        backgroundColor: "transparent"
      }
    }), fc ? /*#__PURE__*/React.createElement("p", {
      className: "pp-foot"
    }, fc.line) : null), dealerRows.length > 0 && /*#__PURE__*/React.createElement(CappedCard, {
      title: `Top ${buyerWord.toLowerCase()}`,
      rows: dealerRows
    }, rows => rows.map(c => /*#__PURE__*/React.createElement(ListRow, {
      key: c.accountNumber,
      name: c.name || c.accountNumber,
      sub: [c.city, c.region].filter(Boolean).join(", ") || c.accountNumber,
      value: usd0(c.totalAmount),
      onClick: isMfg ? () => this.openCustomer(c.accountNumber) : undefined,
      title: isMfg ? `Open ${c.name}` : undefined
    }))), repsRows.length > 0 && /*#__PURE__*/React.createElement(CappedCard, {
      title: "Top reps",
      rows: repsRows
    }, rows => rows.map(r => /*#__PURE__*/React.createElement(ListRow, {
      key: r.repCode,
      name: r.name || r.repCode,
      sub: `${num(r.customers)} ${isMfg ? "dealers" : "customers"} · ${num(r.quantity)} units`,
      value: usd0(r.totalAmount),
      onClick: () => this.openRep(r.repCode),
      title: `Open ${r.name || r.repCode}`
    }))), coItems.length > 0 && /*#__PURE__*/React.createElement(CappedCard, {
      title: "Often ordered with it",
      hint: `bought by the same ${isMfg ? "dealers" : "customers"}`,
      rows: coItems,
      allLabel: `See ${coItems.length}`
    }, rows => rows.map((c, i) => /*#__PURE__*/React.createElement(ListRow, {
      key: c.id,
      name: `${c.sku && c.sku !== c.id ? c.sku : c.id} · ${displayTitle(c.title)}`,
      sub: [c.category && c.category !== "(uncategorized)" ? displayTitle(c.category) : null, i === 0 ? "strongest match" : null].filter(Boolean).join(" · ") || null,
      onClick: () => this.openProduct(c.id, c.title),
      title: `Open ${displayTitle(c.title)}`
    }))), configs.length > 0 && (() => {
      // Most ordered options: fabric/finish components this style sold with
      // (weighted 1/n across frames on shared orders; names are the client's own strings).
      const styleUnits = configs.reduce((s, c) => s + (Number(c.units) || 0), 0) || 1;
      return /*#__PURE__*/React.createElement(CappedCard, {
        title: "Most ordered options",
        hint: "fabrics and finishes on these orders",
        rows: configs,
        span: marketRows.length ? 7 : 12
      }, rows => /*#__PURE__*/React.createElement("div", {
        className: "tblwrap"
      }, /*#__PURE__*/React.createElement("table", {
        className: "territory-table wa-table pp-table"
      }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Option"), /*#__PURE__*/React.createElement("th", null, "Type"), /*#__PURE__*/React.createElement("th", {
        className: "n"
      }, "Orders"), /*#__PURE__*/React.createElement("th", {
        className: "n"
      }, "Revenue"), /*#__PURE__*/React.createElement("th", {
        className: "n"
      }, "Share"))), /*#__PURE__*/React.createElement("tbody", null, rows.map((c, i) => {
        const o = Number(c.orders) || 0;
        return /*#__PURE__*/React.createElement("tr", {
          key: `${c.componentCode}|${i}`
        }, /*#__PURE__*/React.createElement("td", {
          className: "name"
        }, displayTitle(c.componentName || c.componentCode) || "Unnamed"), /*#__PURE__*/React.createElement("td", null, c.componentKind ? titleCase(c.componentKind) : ""), /*#__PURE__*/React.createElement("td", {
          className: "n"
        }, o > 0 && o < 1 ? "under 1" : num(Math.round(o))), /*#__PURE__*/React.createElement("td", {
          className: "n"
        }, usd0(c.revenue)), /*#__PURE__*/React.createElement("td", {
          className: "n"
        }, Math.round((Number(c.units) || 0) / styleUnits * 100), "%"));
      })))));
    })(), marketRows.length > 0 && /*#__PURE__*/React.createElement(CappedCard, {
      title: "Where it sells",
      hint: "by revenue",
      rows: marketRows,
      span: configs.length ? 5 : 12
    }, rows => /*#__PURE__*/React.createElement("div", {
      className: "tblwrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table pp-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Market"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, buyerWord), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Units"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Revenue"))), /*#__PURE__*/React.createElement("tbody", null, rows.map(t => /*#__PURE__*/React.createElement("tr", {
      key: t.region,
      className: "clickable",
      onClick: () => this.openRegion(t.region),
      title: `Open ${t.region}`
    }, /*#__PURE__*/React.createElement("td", {
      className: "name"
    }, US_STATES[String(t.region || "").toUpperCase()] || t.region), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, num(t.activeDealers)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, num(t.quantity)), /*#__PURE__*/React.createElement("td", {
      className: "n strong"
    }, usd0(t.totalAmount)))))))), contacts.length > 0 && /*#__PURE__*/React.createElement(CappedCard, {
      title: "Website interest",
      hint: "people who looked at this product",
      rows: contacts,
      span: 12
    }, rows => /*#__PURE__*/React.createElement("div", {
      className: "tblwrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table pp-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Person"), /*#__PURE__*/React.createElement("th", null, "Company"), /*#__PURE__*/React.createElement("th", null, "Stage"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Views"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Visits"), /*#__PURE__*/React.createElement("th", null, "Last looked"))), /*#__PURE__*/React.createElement("tbody", null, rows.map((c, i) => {
      const stage = String(c.lifecycleStage || "").toLowerCase();
      return /*#__PURE__*/React.createElement("tr", {
        key: String(c.personId) || i,
        className: "clickable cust-row",
        onClick: () => this.props.history.push(`/Contacts/Detail/${String(c.personId)}/${range}`),
        title: "Open contact"
      }, /*#__PURE__*/React.createElement("td", {
        className: "name"
      }, c.name ? titleCase(c.name) : c.email || "Unknown"), /*#__PURE__*/React.createElement("td", null, c.company ? titleCase(c.company) : ""), /*#__PURE__*/React.createElement("td", null, stage ? /*#__PURE__*/React.createElement("span", {
        className: "cust-stage " + stage
      }, stage) : ""), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, num(c.views)), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, num(c.sessions)), /*#__PURE__*/React.createElement("td", null, c.lastViewed ? String(c.lastViewed).slice(0, 10) : ""));
    }))))), inventory && (inventory.inventory || []).length > 0 && (() => {
      // Per-product inventory position + stock trend (INVENTORY_UX.md).
      const pts = inventory.inventory;
      const last = pts[pts.length - 1] || {};
      const onHand = Number(last.qtyAvailable) || 0;
      const inTransit = Number(last.inTransit) || 0;
      const atp = Number(last.atp) || 0;
      const backordered = Number(last.backordered) || 0;
      const demand = (inventory.sales || []).reduce((a, s) => a + (Number(s.units) || 0), 0);
      const stl = atp <= 0 ? "Out of stock" : backordered > 0 ? "Backordered" : "In stock";
      return /*#__PURE__*/React.createElement(Card, {
        title: "Inventory",
        span: 12,
        cls: "pp-card",
        right: /*#__PURE__*/React.createElement(Pill, {
          tone: atp <= 0 ? "danger" : backordered > 0 ? "warning" : "success"
        }, stl)
      }, /*#__PURE__*/React.createElement(PPRibbon, {
        tiles: [{
          l: "On hand",
          v: num(onHand)
        }, {
          l: "In transit",
          v: num(inTransit)
        }, {
          l: "Available to promise",
          v: num(atp)
        }, backordered > 0 ? {
          l: "Backordered",
          v: num(backordered)
        } : null, {
          l: "Units sold this year",
          v: num(demand)
        }]
      }), pts.length > 1 ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("p", {
        className: "pp-foot"
      }, "Available to promise by day. Shaded spans were out of stock; the marker shows the next arrival."), /*#__PURE__*/React.createElement(AtpHistoryChart, {
        inventory: pts,
        nextAvailable: inventory.nextAvailable,
        inboundQty: inventory.inboundQty != null ? inventory.inboundQty : inTransit
      })) : null, inventory.seriesStart ? /*#__PURE__*/React.createElement("p", {
        className: "pp-foot"
      }, "Inventory history starts ", inventory.seriesStart, " and fills in as the feed runs.") : null);
    })()));
  }
}

// "$2,881" average order for a totals row (no cents).
function aovShort(t) {
  const o = Number(t.orders) || 0;
  return o > 0 ? usd0((Number(t.totalAmount) || 0) / o) : "";
}
export default withRouter(ProductDetailViewComponent);